// AgreementTotals.tsx
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import InfoIcon from '@mui/icons-material/Info'
import { alpha, Avatar, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import PandaDocViewer from 'components/PaymentSchedule/PandDocViewer'
import ProductItem from 'components/ProductRows/ProductItem'
import dayjs from 'dayjs'
import React from 'react'
import VersionChip from 'theme/components/Chip/VersionChip'
import { Approver, CurrentUser, NewDeal, OrganizationTableItem, Product, Totals, ValidationErrors } from 'types'

type AgreementTotalsProps = {
  // data
  activeUser: CurrentUser
  deal: NewDeal
  organization: OrganizationTableItem
  productRows: Product[]
  totals: Totals
  validationErrors: ValidationErrors
  approverObjects?: Approver[]
  /** Parent org's custom margin/pricing products, fetched per-PK by
   *  the parent useDealData and passed in. Was previously read from
   *  useOrganizationView -> global cache here, which caused the
   *  initial-load flicker. */
  parentCustomProducts?: any[]
  // flags
  isDraft?: boolean
  isCurrentAuthorizedRecipient?: boolean
  isCurrentContactRecipient?: boolean
  // (optional) PandaDoc/document params if you need to show a link
  document?: any
  // handlers
  onSave?: (e?: React.FormEvent | React.MouseEvent) => void
}

const fmt = (v: number | string) =>
  typeof v === 'number' ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : v

// ─── Reusable sub-components ───────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: 'text.secondary',
    }}
  >
    {children}
  </Typography>
)

const FieldRow = ({
  label,
  value,
  color,
  error,
  tooltip,
}: {
  label: string
  value: React.ReactNode
  color?: string
  error?: boolean
  tooltip?: string
}) => {
  const content = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.4,
        minHeight: 28,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: 11,
          color: error ? 'error.main' : 'text.secondary',
          flexShrink: 0,
          mr: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: color || 'text.primary',
          textAlign: 'right',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  )

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="left">
        <Box sx={{ cursor: 'default' }}>{content}</Box>
      </Tooltip>
    )
  }
  return content
}

const StatCard = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => {
  const theme = useTheme()
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 1,
        px: 1,
        borderRadius: 1.5,
        bgcolor: highlight
          ? alpha(theme.palette.text.highlight, 0.08)
          : alpha(theme.palette.text.primary, 0.03),
        border: '1px solid',
        borderColor: highlight ? alpha(theme.palette.text.highlight, 0.2) : 'divider',
        transition: 'all 0.15s ease',
        '&:hover': {
          borderColor: highlight ? alpha(theme.palette.text.highlight, 0.35) : 'text.secondary',
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'text.secondary',
          display: 'block',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 700,
          color: highlight ? 'primary.main' : 'text.primary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value ?? '—'}
      </Typography>
    </Box>
  )
}

// ─── Main component ────────────────────────────────────────────────

const AgreementTotalsView: React.FC<AgreementTotalsProps> = ({
  activeUser,
  deal,
  organization,
  productRows,
  totals,
  validationErrors,
  approverObjects,
  isDraft,
  isCurrentAuthorizedRecipient,
  isCurrentContactRecipient,
  document,
  onSave,
  parentCustomProducts,
}) => {
  const theme = useTheme()
  // existingCustomProducts now comes from the parent useDealData via
  // the parentCustomProducts prop — sourced per-PK, populates fast on
  // hard refresh, doesn't flicker. The previous useOrganizationView
  // call (global-cache backed) is left here for any other fields the
  // component may have read off it, but custom-products specifically
  // reads from the prop now.
  const existingCustomProducts = parentCustomProducts ?? []

  const entityName = organization?.dba || organization?.name || deal?.dba || deal?.name || '—'
  const isCSA = deal && ['CSA', 'RCSA'].includes(deal.type)
  const accentSecondary = (theme.palette.text as any)?.ai_accent_secondary ?? theme.palette.primary.dark

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {/* ─── Header ──────────────────────────────────────────── */}
      <Box
        sx={{
          p: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${alpha(theme.palette.text.highlight, 0.08)}, ${alpha(accentSecondary, 0.05)})`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Tooltip title={entityName} placement="top-start">
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: 15,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
                flex: 1,
              }}
            >
              {entityName}
            </Typography>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1, flexShrink: 0 }}>
            {approverObjects?.length > 0 && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.75, display: 'block' }}>
                      Approvers
                    </Typography>
                    {approverObjects.map(a => (
                      <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Avatar src={a.image} sx={{ width: 20, height: 20 }} />
                        <Typography variant="caption">{a.name}</Typography>
                      </Box>
                    ))}
                  </Box>
                }
              >
                <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'pointer' }} />
              </Tooltip>
            )}
            {deal?.contractId && (
              <PandaDocViewer
                contractId={deal.contractId}
                customFileName={`${deal.name}`}
                disabled={!deal.contractId}
              />
            )}
          </Box>
        </Box>

        {/* Type + Status chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {deal?.type && (
            <Chip
              label={deal.type}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                color: renderColorByString(deal.type),
                borderColor: renderColorByString(deal.type),
                bgcolor: 'transparent',
              }}
              variant="outlined"
            />
          )}
          {deal?.status && (
            <Chip
              label={deal.status}
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 600,
                color: renderColorByString(deal.status),
                borderColor: renderColorByString(deal.status),
                bgcolor: 'transparent',
              }}
              variant="outlined"
            />
          )}
          {deal?.semanticVersion && <VersionChip version={deal.semanticVersion} />}
        </Box>
      </Box>

      {/* ─── Scrollable content ──────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Dates Section */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <CalendarTodayIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
            <SectionLabel>Dates</SectionLabel>
          </Box>
          <Box
            sx={{
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 0.5,
            }}
          >
            <FieldRow
              label="Start Date"
              value={deal?.startDate ? dayjs(deal.startDate).format('MMM D, YYYY') : undefined}
              error={validationErrors.startDate}
            />
            <FieldRow
              label="End Date"
              value={deal?.endDate ? dayjs(deal.endDate).format('MMM D, YYYY') : undefined}
              error={validationErrors.endDate}
            />
            <FieldRow
              label="Dependencies Due"
              value={deal?.dependenciesDueDate ? dayjs(deal.dependenciesDueDate).format('MMM D, YYYY') : undefined}
              error={validationErrors.dependenciesDueDate}
            />
            <FieldRow
              label="Target Launch"
              value={deal?.targetLaunchDate ? dayjs(deal.targetLaunchDate).format('MMM D, YYYY') : undefined}
              error={validationErrors.targetLaunchDate}
            />
          </Box>
        </Box>

        {/* Recipients Section */}
        <Box>
          <SectionLabel>Recipients</SectionLabel>
          <Box
            sx={{
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              px: 1.5,
              py: 0.5,
            }}
          >
            <FieldRow
              label="Contact"
              value={deal?.recipients?.contactRecipient?.map((r: any) => r?.name || r?.email).join(', ')}
              error={validationErrors.contactRecipient}
              tooltip={deal?.recipients?.contactRecipient?.map((r: any) => r?.email).join(', ') || '—'}
            />
            <FieldRow
              label="GRO Authorized"
              value={deal?.recipients?.authorizedRecipient?.map((r: any) => r?.name || r?.email).join(', ')}
              error={validationErrors.authorizedRecipient}
              tooltip={deal?.recipients?.authorizedRecipient?.map((r: any) => r?.email).join(', ') || '—'}
            />
          </Box>
        </Box>

        {/* CSA Payments */}
        {isCSA && deal?.numberOfPayments && (
          <Box>
            <SectionLabel>Payment Schedule</SectionLabel>
            <Box
              sx={{
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.5,
              }}
            >
              <FieldRow label="# of Payments" value={deal.numberOfPayments} />
              <FieldRow
                label="Per-Payment"
                value={
                  (totals as any)?.paymentAmount?.toLocaleString?.(undefined, {
                    style: 'currency',
                    currency: 'USD',
                  }) ?? '—'
                }
              />
            </Box>
          </Box>
        )}

        {/* Custom Margins/Pricing */}
        {deal && existingCustomProducts?.length > 0 && !isCSA && (
          <Box>
            <SectionLabel>Custom Margins/Pricing</SectionLabel>
            <Box
              sx={{
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                p: 1,
              }}
            >
              {existingCustomProducts.map(p => (
                <Box key={`${p?.PK}-${p?.SK}`}>
                  <ProductItem option={p} minimize={true} />
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default AgreementTotalsView
